import { useState } from 'react';

import type { ErrorLike } from '@apollo/client';
import { useMutation } from '@apollo/client/react';
import { Box } from '@mui/material';
import type {
  GridColDef,
  GridRowOrderChangeParams,
  GridRowParams,
  GridRowsProp,
} from '@mui/x-data-grid-premium';
import { DataGridPremium, GridActionsCellItem } from '@mui/x-data-grid-premium';
import { Trash2Icon } from 'lucide-react';
import { Link, Route, Routes, useSearchParams } from 'react-router-dom';

import type { OverviewGetTeamQuery } from '@graphql/__generated__/graphql';
import { UserInfo } from '@modules/_shared/components/data-grid/UserInfo';
import { MutationErrorSnackbar } from '@modules/_shared/components/MutationErrorModal';
import RatingDisplay from '@modules/_shared/components/RatingDisplay';
import { TimeAgo } from '@modules/_shared/components/TimeAgo';
import { UserSelect } from '@modules/_shared/components/user-select/UserSelect';
import {
  ADD_TEAM_USER,
  DELETE_TEAM_USER,
  OVERVIEW_GET_TEAM,
  REORDER_TEAM_USERS,
  UPDATE_TEAM_USER,
} from '@modules/teams/teamQueries';
import { UserDrawer } from '@modules/users/UserDrawer';
import { useLocale } from '@shared/hooks/locale';

type TeamUser = NonNullable<
  OverviewGetTeamQuery['teams_by_pk']
>['teams_users'][0];

interface TeamUsersProps {
  team: OverviewGetTeamQuery['teams_by_pk'];
  loading?: boolean;
}

export const TeamUsers = ({ team, loading }: TeamUsersProps) => {
  const { t } = useLocale();
  const [searchParams] = useSearchParams();
  const [apolloError, setErrorLike] = useState<ErrorLike | null>(null);

  const refetchQueries = [
    {
      query: OVERVIEW_GET_TEAM,
      variables: { id: team?.id, includeSubscriptions: false },
    },
  ];

  const [deleteTeamUser] = useMutation(DELETE_TEAM_USER);
  const [addTeamUser] = useMutation(ADD_TEAM_USER, {
    refetchQueries,
  });
  const [updateTeamUser] = useMutation(UPDATE_TEAM_USER, {
    refetchQueries,
  });

  const [updateTeamUserOrder] = useMutation(REORDER_TEAM_USERS, {
    refetchQueries,
  });

  const rows: GridRowsProp<TeamUser> = team?.teams_users ?? [];

  const handleRowOrderChange = async (params: GridRowOrderChangeParams) => {
    await updateTeamUserOrder({
      variables: {
        team_id: team?.id ?? '',
        user_id: rows[params.oldIndex].user.id,
        target_position: params.targetIndex,
      },
    });
  };

  const columns: GridColDef<TeamUser>[] = [
    {
      field: 'user',
      headerName: t('User'),
      width: 200,
      renderCell: params => (
        <Link
          to={{
            pathname: `users/${params.row.user.id}`,
            search: searchParams.toString(),
          }}
        >
          <UserInfo user={params.row.user} />
        </Link>
      ),
      sortable: false,
    },
    {
      field: 'title',
      headerName: t('Title'),
      width: 180,
      editable: true,
      sortable: false,
    },
    {
      field: 'reviews',
      headerName: t('Reviews'),
      width: 150,
      renderCell: ({ row: { user } }) =>
        (user.reviews_aggregate.aggregate?.count ?? 0) > 0 && (
          <Box sx={{ gap: 1, alignItems: 'center', display: 'flex' }}>
            <RatingDisplay
              readOnly
              precision={0.1}
              rating={user.reviews_aggregate?.aggregate?.avg?.rating}
              count={user.reviews_aggregate?.aggregate?.count}
              size="small"
            />
          </Box>
        ),
    },
    {
      field: 'added',
      headerName: t('Added'),
      renderCell: params => <TimeAgo dateString={params.row.created_at} />,
      sortable: false,
    },
    {
      field: 'leads',
      headerName: t('Leads'),
      renderCell: params =>
        params.row.user.broker_leads_aggregate.aggregate?.count,
      sortable: false,
    },
    {
      field: 'lots',
      headerName: t('Lots'),
      renderCell: params => params.row.user.lots_aggregate.aggregate?.count,
      sortable: false,
    },
    {
      field: 'permissions',
      headerName: t('Permissions'),
      renderCell: params =>
        [
          params.row.user.is_admin ? t('Admin') : undefined,
          params.row.user.is_broker ? t('Broker') : undefined,
        ]
          .filter(Boolean)
          .join(', '),
      sortable: false,
    },
    {
      field: 'status',
      width: 80,
      headerName: t('Status'),
      getOptionValue: (value: any) => value.code,
      getOptionLabel: (value: any) => value.name,
      valueOptions: [
        { code: 'active', name: t('Active') },
        { code: 'deactivated', name: t('Deactivated') },
      ],
      valueGetter: (_, row) => (row.deactivated ? 'deactivated' : 'active'),
      valueSetter: (value, row) => {
        return { ...row, deactivated: value === 'deactivated' };
      },
      type: 'singleSelect',
      sortable: false,
      editable: true,
    },
    {
      field: 'team_role',
      headerName: t('Team Role'),
      width: 80,
      getOptionValue: (value: any) => value.code,
      getOptionLabel: (value: any) => value.name,
      valueOptions: [
        { code: 'leader', name: t('Leader') },
        { code: 'member', name: t('Member') },
      ],
      valueGetter: (_, row) => (row.is_leader ? 'leader' : 'member'),
      valueSetter: (value, row) => {
        return { ...row, is_leader: value === 'leader' };
      },
      type: 'singleSelect',
      sortable: false,
      editable: true,
    },
    {
      field: 'actions',
      type: 'actions',
      getActions: (params: GridRowParams) => [
        <GridActionsCellItem
          icon={<Trash2Icon size={18} />}
          label="Delete"
          onClick={() =>
            deleteTeamUser({
              variables: {
                team_id: team?.id ?? '',
                user_id: params.row.user.id,
              },
              refetchQueries,
              onError: setErrorLike,
            })
          }
          color="inherit"
        />,
      ],
    },
  ];

  return (
    <>
      <Box sx={{ mb: 1, maxWidth: 300 }}>
        <UserSelect
          clearOnSelect
          onChange={userId =>
            addTeamUser({
              variables: {
                user_id: userId ?? '',
                team_id: team?.id ?? '',
              },
            })
          }
          InputProps={{
            placeholder: t('Add user'),
          }}
        />
      </Box>
      <DataGridPremium
        slotProps={{
          loadingOverlay: {
            variant: 'skeleton',
            noRowsVariant: 'skeleton',
          },
        }}
        autoHeight
        rows={rows}
        columns={columns}
        loading={loading}
        rowReordering={true}
        onRowOrderChange={newModel => handleRowOrderChange(newModel)}
        getRowId={row => row.user.id}
        rowHeight={64}
        sx={{
          backgroundColor: 'background.paper',
          borderRadius: 4,
        }}
        processRowUpdate={async (newModel, oldModel) => {
          // update title, is_leader or deactivated has changed
          if (
            newModel.title !== oldModel.title ||
            newModel.is_leader !== oldModel.is_leader ||
            newModel.deactivated !== oldModel.deactivated
          ) {
            try {
              await updateTeamUser({
                variables: {
                  team_id: team?.id ?? '',
                  user_id: newModel.user.id,
                  team_user: {
                    title: newModel.title,
                    is_leader: newModel.is_leader,
                    deactivated: newModel.deactivated,
                  },
                },
              });
            } catch (error) {
              setErrorLike(error as ErrorLike);
              return oldModel;
            }
          }
          return newModel;
        }}
      />

      <MutationErrorSnackbar
        error={apolloError}
        onClose={() => setErrorLike(null)}
      />

      <Routes>
        <Route
          path="users/:userId/*"
          element={<UserDrawer maxWidth={1200} />}
        />
      </Routes>
    </>
  );
};
